import React, { useMemo } from 'react';
import { Stack } from 'components/stack';
import { MedicalPrescriptionProgramInfoDate } from 'components/medical-prescription/components/program-info-date';
import { ShortRemark } from 'components/short-remark';
import { MedicalPrescriptionLastDate } from 'components/medical-prescription';
import { Box } from '@mui/material';
import { TagSurvey } from 'components/dialoog-user-patient-profile-session';
import { useTranslate } from 'hooks/use-translate';
import { apiUserPatientProfile } from 'services/user-patient-profile';
import { useEffectError } from 'hooks';
import { TemplatePatientFullName } from 'components/templates/template-patient-full-name';
import { makeFullName } from 'utils/app-helpers';
import style from './index.module.scss';

interface Props {
  userPatientProfileID: string;
}
export const NotebookPatientInfo: React.FC<React.PropsWithChildren<Props>> = ({
  userPatientProfileID,
}) => {
  const { tp } = useTranslate();
  const { data: patient, error } =
    apiUserPatientProfile.useGetPatientForGeneralFormQuery(userPatientProfileID);
  useEffectError(error);

  const { maintenanceWeight, shortRemark } = patient || {};

  const patientInfo = useMemo(() => {
    if (!patient) return undefined;
    const fullName = makeFullName(patient);
    return { ...patient, fullName };
  }, [patient]);

  const title = useMemo(() => {
    return [patientInfo?.fullName, patientInfo?.idNumber].filter(Boolean).join(' | ');
  }, [patientInfo]);

  return (
    <Stack
      className={style.root}
      spacing={1}
      sx={{
        alignItems: 'center',
      }}
    >
      <div>
        <TemplatePatientFullName variant={'h6'} patient={patientInfo}>
          {title}
        </TemplatePatientFullName>
      </div>
      <div>
        <MedicalPrescriptionProgramInfoDate userPatientProfileID={userPatientProfileID} />
      </div>
      {maintenanceWeight && (
        <ShortRemark priority={'high'}>
          {tp('maintenance-weight')}: {maintenanceWeight}
        </ShortRemark>
      )}
      {shortRemark && (
        <ShortRemark title={tp('short-remark')} priority={'high'}>
          {shortRemark}
        </ShortRemark>
      )}
      <div>
        <MedicalPrescriptionLastDate
          userPatientProfileID={userPatientProfileID}
          userPatientProfilePrescriptionID={null}
        />
      </div>
      <Box
        sx={{
          ml: 'auto',
        }}
      >
        <TagSurvey userPatientProfileID={userPatientProfileID} />
      </Box>
    </Stack>
  );
};
